/* eslint-disable @typescript-eslint/indent */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Icon, IconButton, LinearProgress, Pagination, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Chip, Box, Typography, useTheme, alpha, Dialog, DialogContent, DialogTitle, Button, Tooltip
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FerramentasDaListagemNfCe, LinhasEsqueletoTabela } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { getRowsPerPageCache, setRowsPerPageCache } from '../../../shared/utils/paginaRowsCache';
import { IListagemNfece, NfeService } from '../../../shared/services/api/Financeiro/fiscal';
import dayjs from 'dayjs';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular, moedaMobile } from '../../../shared/components/listagem-mobile';

import { BotaoHistoricoEmissao, DialogHistoricoEmissao } from '../HistoricoEmissao/HistoricoEmissao';

export const ListagemDeNfce: React.FC = () => {
  const listaCelular = useCelular();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const ROWS_CACHE_KEY = 'nfce';
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPageCache(ROWS_CACHE_KEY) ?? Environment.LIMITE_DE_LINHASSMALL);
  const [availableHeight, setAvailableHeight] = useState<number>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const [rows, setRows] = useState<IListagemNfece[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [istexto, setIsTexto] = useState(false);
  const [isdate, setIsDate] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  
  const [openPdfViewer, setOpenPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [selectedNfeNumber, setSelectedNfeNumber] = useState<number>(0);
  // Histórico de tentativas de emissão (ícone na coluna Ações).
  const [historicoAberto, setHistoricoAberto] = useState<{ id: string; titulo: string } | null>(null);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };

  const busca = useMemo(() => {
    return searchParams.get('busca') || '';
  }, [searchParams]);

  const basicselect = useMemo(() => {
    return searchParams.get('basicselect') || 'n.dhemi';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  useEffect(() => {
    if (basicselect === 'n.dhemi') {
      setIsTexto(false);
      setIsDate(true);
    } else {
      setIsTexto(true);
      setIsDate(false);
    }
  }, [basicselect]);

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      NfeService.getAllNFCe(pagina, busca, basicselect, rowsPerPage)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    });
  }, [busca, pagina, basicselect, rowsPerPage]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ busca, basicselect, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);

  const handleReimprimir = async (id: string, nfeNumber: number, event: React.MouseEvent) => {
    event.stopPropagation(); 
    
    setIsLoadingPdf(true);
    setSelectedNfeNumber(nfeNumber);
    
    try {
      const response = await NfeService.gerarPDFNFCe(id);
      
      if (response instanceof Error) {
        setTms('error');
        setMsg(response.message);
        setOpenmsg(true);
        setOpenPdfViewer(false);
      } else {
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setOpenPdfViewer(true);
      }
    } catch (error) {
      setTms('error');
      setMsg('Erro ao gerar o PDF da NFC-e');
      setOpenmsg(true);
    } finally {
      setIsLoadingPdf(false);
    }
  };
  const handleClosePdfViewer = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl); 
    }
    setOpenPdfViewer(false);
    setPdfUrl(null);
    setSelectedNfeNumber(0);
  };

  

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Altura de uma linha nesta tabela: o Chip de status (26px normal / 20px denso) + o padding
  // vertical da célula é quem domina a altura real da linha, não o texto. Usamos o mesmo
  // limiar de 480px do "dense" abaixo para escolher qual altura considerar.
  const DENSE_THRESHOLD = 480;
  const ALTURA_LINHA_NORMAL = 50; // chip 26px + padding 10px*2 + borda
  const ALTURA_LINHA_DENSE = 28; // chip 20px + padding 2px*2 + borda

  const calcularLimite = () => {
    const container = containerRef.current;
    if (!container) return;

    // Mede o espaço real até o rodapé da janela em vez de usar um valor fixo de
    // "calc(100vh - Npx)", que fica desalinhado com a altura real do título/toolbar.
    const BOTTOM_GAP = 16;
    const alturaContainer = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - BOTTOM_GAP);

    const headerEl = container.querySelector('thead');
    const alturaHeader = headerEl ? headerEl.getBoundingClientRect().height : 34;

    // Reservada como constante (não medida do <tfoot>): a paginação só existe no DOM quando
    // totalCount > rowsPerPage, o que criaria uma dependência circular com o próprio cálculo.
    const FOOTER_RESERVADO = 56;
    const MIN_ROWS = 1;
    const MAX_ROWS = 100;
    const FOLGA_LINHAS = 1;
    const alturaLinha = alturaContainer < DENSE_THRESHOLD ? ALTURA_LINHA_DENSE : ALTURA_LINHA_NORMAL;
    const alturaDisponivel = alturaContainer - alturaHeader - FOOTER_RESERVADO;
    const novoLimite = Math.min(
      MAX_ROWS,
      Math.max(MIN_ROWS, Math.floor(alturaDisponivel / alturaLinha) - FOLGA_LINHAS)
    );

    setAvailableHeight(prev => (prev === alturaContainer ? prev : alturaContainer));
    setRowsPerPageCache(ROWS_CACHE_KEY, novoLimite);
    setRowsPerPage(prev => (prev === novoLimite ? prev : novoLimite));
  };

  // useLayoutEffect para rodar antes da pintura e antes do efeito de busca (que depende de
  // rowsPerPage), garantindo que a primeira requisição já saia com o limite correto.
  useLayoutEffect(() => {
    calcularLimite();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', calcularLimite);
    return () => window.removeEventListener('resize', calcularLimite);
  }, []);

  // Tier de compactação de acordo com a largura real do container (não da janela),
  // já que o menu lateral reduz o espaço disponível para a tabela.
  const tier = (() => {
    if (containerWidth === 0) return 3;
    if (containerWidth < 380) return 0;
    if (containerWidth < 480) return 1;
    if (containerWidth < 600) return 2;
    if (containerWidth < 760) return 3;
    if (containerWidth < 950) return 4;
    if (containerWidth < 1150) return 5;
    return 6;
  })();

  const compactStatus = tier <= 1; // < 480px: chip de status só com ícone

  // Quando a altura visível do container é curta, comprime o padding/fonte das
  // linhas para caber mais registros sem precisar de scroll vertical.
  const dense = availableHeight !== undefined && availableHeight < DENSE_THRESHOLD;
  const cellPaddingY = dense ? '3px' : '10px';
  const cellPaddingYAction = dense ? '2px' : '10px';
  const cellFontSizeSmall = dense ? '0.62rem' : '0.75rem';
  const cellFontSizeMedium = dense ? '0.65rem' : '0.8rem';
  const headerPaddingY = dense ? '4px' : '10px';
  const headerFontSize = dense ? '0.62rem' : '0.75rem';
  const chipHeight = dense ? 20 : 26;
  const iconButtonPadding = dense ? '3px' : '8px';

  const dataSelecionada = useMemo(() => {
    if (!busca) return null;
    return dayjs(busca, 'YYYY-MM-DD');
  }, [busca]);
  
  const getStatusNfe = (status: number) => {
    switch (status) {
      case 2:
        return {
          label: 'Autorizada',
          color: 'success' as const,
          iconColor: theme.palette.success.main,
          icon: 'check_circle'
        };
      case 5:
        return {
          label: 'Denegada',
          color: 'warning' as const,
          iconColor: theme.palette.warning.main,
          icon: 'warning'
        };
      case 3:
        return {
          label: 'Cancelada',
          color: 'error' as const,
          iconColor: theme.palette.error.main,
          icon: 'cancel'
        };
      case 4:
        return {
          label: 'Inutilizada',
          color: 'default' as const,
          iconColor: theme.palette.text.secondary,
          icon: 'block'
        };
      default:
        return {
          label: 'Pendente',
          color: 'info' as const,
          iconColor: theme.palette.info.main,
          icon: 'pending'
        };
    }
  };
  
  return (
    <LayoutBaseDePagina
      titulo="Listagem de NFC-e"
      barraDeFerramentas={
        <FerramentasDaListagemNfCe
          mostrarInputBusca={istexto}
          mostrarInputData={isdate}
          mostrarInputBasicSelect
          textoDaBusca={busca}
          textoDoData={dataSelecionada}
          textoDoSelect={basicselect}
          textoBotaoNovo="Adicionar"
          aoClicarEmNovo={() => navigate('/nfe/novo')}
          aoMudarTextoDeBusca={texto =>
            setSearchParams(
              { busca: texto, basicselect, pagina: '1' },
              { replace: true }
            )
          }
          aoMudarData={novaData => {
            const formattedDate = novaData
              ? dayjs(novaData).format('YYYY-MM-DD')
              : '';
            setSearchParams(
              { busca: formattedDate, basicselect, pagina: '1' },
              { replace: true }
            );
          }}
          aoMudarTextoDoSelect={textoselect =>
            setSearchParams(
              { busca: '', basicselect: textoselect, pagina: '1' },
              { replace: true }
            )
          }
        />
      }
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={tms === 'error' ? null : 900}
        onClose={handleCloseMsg}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      {/* Modal do Visualizador de PDF */}
      <Dialog
        open={openPdfViewer}
        onClose={handleClosePdfViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 1.5
        }}>
          <Typography variant="h6">
            Visualização da NFC-e {selectedNfeNumber}
          </Typography>
          <IconButton
            onClick={handleClosePdfViewer}
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main
              }
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          {isLoadingPdf ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
              <LinearProgress sx={{ width: '50%', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Carregando documento...
              </Typography>
            </Box>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title={`NFC-e ${selectedNfeNumber}`}
            />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <Typography variant="body1" color="error">
                Erro ao carregar o documento
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <TableContainer
        ref={containerRef}
        component={Paper}
        variant="outlined"
        sx={{
          ml: { xs: 1, sm: 2 },
          mt: 1,
          mb: 1,
          borderRadius: 2.5,
          overflow: 'auto',
          maxHeight: availableHeight ? `${availableHeight}px` : 'calc(100vh - 240px)',
          width: { xs: 'calc(100% - 8px)', sm: 'calc(99% - 16px)' },
          boxShadow: theme.shadows[2],
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha(theme.palette.text.primary, 0.4)} ${alpha(
            theme.palette.background.paper,
            0.6
          )}`,
          '&::-webkit-scrollbar': {
            height: 8,
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(
              theme.palette.background.paper,
              0.6
            ),
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(
              theme.palette.text.primary,
              theme.palette.mode === 'dark' ? 0.4 : 0.3
            ),
            borderRadius: 8,
            transition: 'background-color 0.2s ease',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: alpha(
              theme.palette.text.primary,
              theme.palette.mode === 'dark' ? 0.6 : 0.5
            ),
          },
        }}
      >
        <Table
          size="medium"
          sx={{
            width: '100%',
            tableLayout: 'auto',
          }}
        >
          {!listaCelular && (
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              '& th': {
                fontSize: headerFontSize,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: theme.palette.text.secondary,
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                borderBottom: `1px solid ${alpha(
                  theme.palette.divider,
                  0.6
                )}`,
                padding: `${headerPaddingY} 8px`,
              },
            }}
          >
            <TableRow>
              <TableCell align="center">Data Emissão</TableCell>
              <TableCell align="right">Nº NFE</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell align="right">Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          )}

          {isLoading && rows.length === 0 && (
            <TableBody>
              <LinhasEsqueletoTabela
                colunas={[
                  { align: 'center', width: '60%' },
                  { align: 'right', width: '50%' },
                  { align: 'right', width: '55%' },
                  { align: 'right', variant: 'rounded', width: 60, height: 22 },
                  { align: 'center', variant: 'circular', width: 24, height: 24 },
                ]}
              />
            </TableBody>
          )}
          <TableBody>
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box py={4} display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <Icon sx={{ fontSize: 40, color: theme.palette.text.secondary }}>
                      search_off
                    </Icon>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Nenhum registro encontrado
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idnfce}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={<Icon fontSize="small">receipt</Icon>}
                      cor={getStatusNfe(row.status).iconColor}
                      titulo={`NFC-e ${row.nfe}`}
                      detalhes={[dayjs(row.dhemi).format('DD/MM/YY')]}
                      valor={moedaMobile(row.vnfe)}
                      corValor="success.main"
                      etiqueta={{ rotulo: getStatusNfe(row.status).label, cor: getStatusNfe(row.status).color }}
                      destaque={row.status === 5}
                      acoes={(
                        <>
                          <BotaoHistoricoEmissao
                            qtdErros={row.qtderros}
                            ultimoMotivo={row.status !== 2 ? row.motivoerro : null}
                            ultimaEtapa={row.etapaerro}
                            onClick={() => setHistoricoAberto({ id: row.idnfce, titulo: `NFC-e ${row.nfe}` })}
                          />
                          <IconButton size="small" color="primary" title="Reimprimir NFC-e" onClick={(e) => handleReimprimir(row.idnfce, row.nfe, e)}>
                            <Icon fontSize="small">print</Icon>
                          </IconButton>
                        </>
                      )}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map(row => {
              const statusInfo = getStatusNfe(row.status);
              const isCancelada = row.status === 5;

              return (
                <TableRow
                  key={row.idnfce}
                  hover
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: isCancelada
                      ? alpha(theme.palette.error.light, 0.05)
                      : theme.palette.background.paper,
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      backgroundColor: isCancelada
                        ? alpha(theme.palette.error.light, 0.08)
                        : alpha(theme.palette.primary.main, 0.12),
                      boxShadow: `0 6px 20px ${alpha(
                        theme.palette.common.black,
                        0.12
                      )}`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: cellFontSizeSmall,
                      padding: `${cellPaddingY} 8px`,
                    }}
                  >
                    {dayjs(row.dhemi).format('DD/MM/YYYY')}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontSize: cellFontSizeSmall,
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      padding: `${cellPaddingY} 8px`,
                    }}
                  >
                    {row.nfe}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontSize: cellFontSizeMedium,
                      fontWeight: 700,
                      color: theme.palette.success.dark,
                      whiteSpace: 'nowrap',
                      padding: `${cellPaddingY} 8px`,
                    }}
                  >
                    {(Number(row.vnfe) || 0).toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>

                  <TableCell align="right" sx={{ padding: `${cellPaddingY} 8px` }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      gap={1}
                    >
                      <Tooltip title={compactStatus ? statusInfo.label : ''} arrow>
                        <Chip
                          label={compactStatus ? undefined : statusInfo.label}
                          size="small"
                          color={statusInfo.color}
                          icon={
                            <Icon
                              sx={{
                                fontSize: 14,
                                color: statusInfo.iconColor,
                              }}
                            >
                              {statusInfo.icon}
                            </Icon>
                          }
                          sx={{
                            height: chipHeight,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            ...(compactStatus && {
                              width: 26,
                              '& .MuiChip-label': { display: 'none' },
                              '& .MuiChip-icon': { margin: 0 },
                            }),
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ padding: `${cellPaddingYAction} 4px`, whiteSpace: 'nowrap' }}>
                    <BotaoHistoricoEmissao
                      qtdErros={row.qtderros}
                      ultimoMotivo={row.status !== 2 ? row.motivoerro : null}
                      ultimaEtapa={row.etapaerro}
                      onClick={() => setHistoricoAberto({ id: row.idnfce, titulo: `NFC-e ${row.nfe}` })}
                    />
                    <IconButton
                      onClick={(e) => handleReimprimir(row.idnfce, row.nfe, e)}
                      size="small"
                      sx={{
                        color: theme.palette.primary.main,
                        p: iconButtonPadding,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          transform: 'scale(1.1)'
                        },
                        transition: 'transform 0.2s ease'
                      }}
                      title="Reimprimir NFC-e"
                    >
                      <Icon fontSize="small">print</Icon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}

            {totalCount > rowsPerPage && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box display="flex" justifyContent="center" py={2}>
                    <Pagination
                      page={pagina}
                      count={Math.ceil(
                        totalCount / rowsPerPage
                      )}
                      onChange={(_, newPage) =>
                        setSearchParams(
                          { busca, basicselect, pagina: newPage.toString() },
                          { replace: true }
                        )
                      }
                      size="small"
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </TableContainer>
      <DialogHistoricoEmissao
        open={!!historicoAberto}
        tipo="nfce"
        iddocumento={historicoAberto?.id || ''}
        titulo={historicoAberto?.titulo || ''}
        onClose={() => setHistoricoAberto(null)}
      />
    </LayoutBaseDePagina>
  );
};